import React, { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { CircularProgress, Alert } from '@mui/material'
import { getPost, trackPostView, togglePostLike } from '../../api/posts'

const PostDetail = () => {
  const { identifier } = useParams()
  const navigate = useNavigate()
  const userId = localStorage.getItem('uid')

  // Fetch post data
  const { data: postData, isLoading, error } = useQuery({
    queryKey: ['post', identifier],
    queryFn: () => getPost(identifier),
    enabled: !!identifier
  })

  // Track view mutation
  const trackViewMutation = useMutation({
    mutationFn: (viewData) => trackPostView(postData?.data?.post?.id, viewData),
    onError: (error) => {
      console.error('Failed to track view:', error)
    }
  })

  // Like toggle mutation
  const likeMutation = useMutation({
    mutationFn: () => togglePostLike(postData?.data?.post?.id, userId),
    onError: (error) => {
      console.error('Failed to toggle like:', error)
    }
  })

  // Extract post from API response
  const post = postData?.data?.post

  // Track view when post loads
  useEffect(() => {
    if (post && userId) {
      trackViewMutation.mutate({
        user_id: userId,
        ip_address: '',
        user_agent: navigator.userAgent
      })
    }
  }, [post, userId])

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleLike = () => {
    if (userId && post) {
      likeMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="post-detail-container">
        <div className="loading-container">
          <CircularProgress size={50} />
          <p>Loading post...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="post-detail-container">
        <Alert severity="error" className="error-alert">
          {error.message || 'Failed to load post'}
        </Alert>
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Go Back
        </button>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="post-detail-container">
        <Alert severity="warning" className="error-alert">
          Post not found
        </Alert>
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Go Back
        </button>
      </div>
    )
  }

  return (
    <div className="post-detail-container">
      {/* Navigation */}
      <div className="post-navigation">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="fi fi-rr-arrow-left"></i>
          Back
        </button>
        <Link to="/dashboard/posts" className="all-posts-link">
          View All Posts
          <i className="fi fi-rr-arrow-right"></i>
        </Link>
      </div>

      {/* Main Post Card */}
      <article className="post-card">
        {/* Featured Image Header */}
        {post.featured_image && (
          <div className="post-hero">
            <img src={post.featured_image} alt={post.title} />
            <div className="hero-overlay">
              <div className="hero-content">
                {post.status && (
                  <span className={`status-badge ${post.status}`}>
                    {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                  </span>
                )}
                <h1 className="post-title">{post.title}</h1>
              </div>
            </div>
          </div>
        )}

        {/* Post Header (when no featured image) */}
        {!post.featured_image && (
          <header className="post-header-simple">
            <div className="header-content">
              {post.status && (
                <span className={`status-badge ${post.status}`}>
                  {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                </span>
              )}
              <h1 className="post-title">{post.title}</h1>
            </div>
          </header>
        )}

        {/* Meta Information Card */}
        <div className="meta-card">
          <div className="author-section">
            <div className="author-avatar">
              <i className="fi fi-rr-user"></i>
            </div>
            <div className="author-info">
              <span className="author-name">
                {post.author?.first_name || 'Unknown'} {post.author?.last_name || 'Author'}
              </span>
              <span className="post-date">
                {post.created_at ? formatDate(post.created_at) : 'Unknown date'}
              </span>
            </div>
          </div>
          
          <div className="engagement-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <i className="fi fi-rr-eye"></i>
              </div>
              <div className="stat-info">
                <span className="stat-number">{post.views_count || 0}</span>
                <span className="stat-label">views</span>
              </div>
            </div>
            
            <button 
              className={`stat-item like-stat ${likeMutation.isPending ? 'loading' : ''}`}
              onClick={handleLike}
              disabled={!userId || likeMutation.isPending}
            >
              <div className="stat-icon">
                <i className="fi fi-rr-heart"></i>
              </div>
              <div className="stat-info">
                <span className="stat-number">{post.likes_count || 0}</span>
                <span className="stat-label">likes</span>
              </div>
            </button>
          </div>
        </div>

        {/* Tags Section */}
        {post.tags && post.tags.length > 0 && (
          <div className="tags-card">
            <h3 className="tags-title">Tags</h3>
            <div className="tags-list">
              {post.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Excerpt Card */}
        {post.excerpt && (
          <div className="excerpt-card">
            <h3 className="excerpt-title">Summary</h3>
            <p className="excerpt-text">{post.excerpt}</p>
          </div>
        )}

        {/* Content Card */}
        <div className="content-card">
          <h3 className="content-title">Article</h3>
          <div 
            className="content-html"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* Action Buttons */}
        <div className="actions-section">
          <button 
            className={`action-button like-button ${likeMutation.isPending ? 'loading' : ''}`}
            onClick={handleLike}
            disabled={!userId || likeMutation.isPending}
          >
            <i className="fi fi-rr-heart"></i>
            {likeMutation.isPending ? 'Loading...' : 'Like'}
          </button>
          
          <button 
            className="action-button share-button"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href)
              alert('Link copied to clipboard!')
            }}
          >
            <i className="fi fi-rr-share"></i>
            Share
          </button>
        </div>

        {/* Updated Info */}
        {post.updated_at && post.created_at && post.updated_at !== post.created_at && (
          <div className="updated-card">
            <i className="fi fi-rr-clock"></i>
            <span>Last updated: {formatDate(post.updated_at)}</span>
          </div>
        )}
      </article>

      <style jsx>{`
        .post-detail-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 24px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #f9fafb;
          min-height: 100vh;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 100px 0;
          gap: 20px;
        }

        .post-navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding: 20px 0;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          border: none;
          padding: 12px 20px;
          color: #475569;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .back-button:hover {
          color: #1f2937;
        }

        .all-posts-link {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #2563eb;
          text-decoration: none;
          font-weight: 600;
          padding: 12px 20px;
          border-radius: 12px;
          transition: all 0.2s;
        }

        .all-posts-link:hover {
          background: #eff6ff;
          transform: translateY(-1px);
        }

        .post-card {
          background: transparent;
          overflow: hidden;
          margin-bottom: 32px;
        }

        .post-hero {
          position: relative;
          height: 400px;
          overflow: hidden;
        }

        .post-hero img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.1));
          display: flex;
          align-items: flex-end;
        }

        .hero-content {
          padding: 48px;
          color: white;
        }

        .post-header-simple {
          padding: 48px;
          background: linear-gradient(to bottom right, #f8fafc, #f1f5f9);
        }

        .header-content {
          text-align: center;
        }

        .status-badge {
          display: inline-block;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 16px;
        }

        .status-badge.published {
          background: linear-gradient(to right, #dcfce7, #bbf7d0);
          color: #15803d;
        }

        .status-badge.draft {
          background: linear-gradient(to right, #fef3c7, #fde68a);
          color: #d97706;
        }

        .status-badge.archived {
          background: linear-gradient(to right, #fee2e2, #fecaca);
          color: #dc2626;
        }

        .post-title {
          font-size: 3rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0;
          line-height: 1.1;
          background: linear-gradient(to right, #1f2937, #374151);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-content .post-title {
          color: white;
          -webkit-text-fill-color: white;
        }

        .meta-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 32px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .author-section {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .author-avatar {
          width: 56px;
          height: 56px;
          background: #8b5cf6;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
        }

        .author-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .author-name {
          font-weight: 700;
          color: #1f2937;
          font-size: 1.1rem;
        }

        .post-date {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .engagement-stats {
          display: flex;
          gap: 24px;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .like-stat {
          background: transparent;
          border: none;
        }

        .like-stat:hover {
          background: transparent;
        }

        .stat-icon {
          width: 40px;
          height: 40px;
          background: #3b82f6;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .like-stat .stat-icon {
          background: #ef4444;
        }

        .stat-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .stat-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.8rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .tags-card,
        .excerpt-card,
        .content-card {
          padding: 32px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .tags-title,
        .excerpt-title,
        .content-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 16px 0;
        }

        .tags-list {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .tag {
          background: #2563eb;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .excerpt-text {
          font-size: 1.125rem;
          line-height: 1.7;
          color: #4b5563;
          margin: 0;
          padding: 24px 0;
        }

        .content-html {
          line-height: 1.8;
          color: #374151;
          font-size: 1.1rem;
        }

        .content-html h1,
        .content-html h2,
        .content-html h3,
        .content-html h4,
        .content-html h5,
        .content-html h6 {
          margin: 2.5rem 0 1rem 0;
          color: #1f2937;
          font-weight: 700;
        }

        .content-html p {
          margin: 1.5rem 0;
        }

        .content-html strong {
          color: #1f2937;
          font-weight: 700;
        }

        .actions-section {
          display: flex;
          gap: 16px;
          padding: 32px 0;
          justify-content: center;
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .action-button:hover {
          opacity: 0.7;
        }

        .like-button {
          color: #ef4444;
        }

        .like-button:hover {
          color: #dc2626;
        }

        .share-button {
          color: #3b82f6;
        }

        .share-button:hover {
          color: #2563eb;
        }

        .action-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .updated-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px 0;
          color: #64748b;
          font-size: 0.9rem;
          justify-content: center;
        }

        .error-alert {
          margin-bottom: 24px;
        }

        @media (max-width: 768px) {
          .post-detail-container {
            padding: 16px;
          }

          .post-navigation {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
            text-align: center;
          }

          .post-title {
            font-size: 2rem;
          }

          .meta-card {
            flex-direction: column;
            gap: 24px;
            align-items: flex-start;
          }

          .engagement-stats {
            width: 100%;
            justify-content: space-between;
          }

          .actions-section {
            flex-direction: column;
            align-items: center;
          }

          .hero-content {
            padding: 32px 24px;
          }

          .tags-card,
          .excerpt-card,
          .content-card {
            padding: 24px;
          }
        }
      `}</style>
    </div>
  )
}

export default PostDetail 